<template>
  <div class="gl-header">
    <!-- Left: Logo + Toggle -->
    <div class="gl-header-brand">
      <SideBar />
    </div>

    <!-- Desktop: Search bar -->
    <div class="gl-header-search gl-desktop-search">
      <searchBar ref="desktopSearch" />
    </div>

    <!-- Right: Actions -->
    <div class="gl-header-actions">

      <!-- Mobile: Search icon -->
      <button class="gl-icon-btn gl-mobile-only" @click="openMobileSearch" title="Search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </button>

      <!-- Notifications -->
      <router-link to="/owner/notifications" class="gl-icon-btn" title="Notifications">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        <span class="gl-notif-dot"></span>
      </router-link>

      <!-- Dark Mode Toggle -->
      <button class="gl-icon-btn" @click="toggleMode" :title="isDark ? 'Light mode' : 'Dark mode'">
        <svg v-if="!isDark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      </button>

      <!-- Divider (desktop only) -->
      <div class="gl-divider gl-desktop-only"></div>

      <!-- User Profile -->
      <div class="gl-user-menu" :class="{ open: userMenuOpen }" @click.stop="userMenuOpen = !userMenuOpen">
        <div class="gl-user-trigger">
          <div class="gl-avatar">{{ userInitials }}</div>
          <div class="gl-user-info gl-desktop-only">
            <span class="gl-user-name">{{ userName }}</span>
            <span class="gl-user-role">{{ userRole }}</span>
          </div>
          <svg class="gl-chevron gl-desktop-only" :class="{ rotated: userMenuOpen }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>

        <div class="gl-dropdown" v-show="userMenuOpen">
          <div class="gl-dropdown-header">
            <div class="gl-avatar lg">{{ userInitials }}</div>
            <div>
              <div class="gl-dd-name">{{ userName }}</div>
              <div class="gl-dd-email">{{ userEmail }}</div>
            </div>
          </div>
          <div class="gl-dropdown-divider"></div>
          <router-link to="/owner/settings" class="gl-dropdown-item" @click="userMenuOpen = false">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            Settings
          </router-link>
          <button class="gl-dropdown-item danger" @click="handleLogout">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign out
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile search overlay -->
    <transition name="gl-search-slide">
      <div v-if="mobileSearchOpen" class="gl-mobile-search-overlay" @click.self="mobileSearchOpen = false">
        <div class="gl-mobile-search-bar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#900C27;flex-shrink:0;">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref="mobileInput"
            v-model="mobileQuery"
            class="gl-mobile-search-input"
            placeholder="Search pages…"
            @input="onMobileSearch"
            @keydown.esc="mobileSearchOpen = false"
            @keydown.enter="goMobileFirst"
            autocomplete="off"
          />
          <button class="gl-mobile-search-close" @click="mobileSearchOpen = false">Cancel</button>
        </div>
        <div v-if="mobileResults.length" class="gl-mobile-search-results">
          <router-link
            v-for="(item, i) in mobileResults.slice(0, 6)"
            :key="i"
            :to="{ path: item.path }"
            class="gl-mobile-result"
            @click="mobileSearchOpen = false"
          >
            <div class="gl-mobile-result-icon">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div>
              <div class="gl-mobile-result-title" v-html="mobileHighlight(item.title)"></div>
              <div class="gl-mobile-result-path">{{ item.path }}</div>
            </div>
          </router-link>
        </div>
        <div v-else-if="mobileQuery" class="gl-mobile-search-empty">No results for "{{ mobileQuery }}"</div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { useAuthStore } from '@/stores/auth';
import { mapActions, mapState as mapPiniaState } from 'pinia';
import SideBar from "./sidebar.vue";
import searchBar from "./searchBar.vue";

export default {
  components: { SideBar, searchBar },

  data() {
    return {
      filtered: false,
      userMenuOpen: false,
      isDark: false,
      mobileSearchOpen: false,
      mobileQuery: '',
      mobileResults: [],
    };
  },

  computed: {
    ...mapState({ menuItems: (state) => state.menu.searchData }),

    userName() {
      const store = useAuthStore();
      return store.userName || 'Admin';
    },
    userEmail() {
      const store = useAuthStore();
      return store.userEmail || '';
    },
    userRole() {
      const store = useAuthStore();
      if (store.isSuperAdmin) return 'Super Admin';
      if (store.isCompanyOwner) return 'Owner';
      return 'Admin';
    },
    userInitials() {
      return this.userName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },
  },

  mounted() {
    const saved = localStorage.getItem('mode');
    this.isDark = saved === 'dark-only';
    document.body.className = this.isDark ? 'dark-only' : 'ltr';
    document.addEventListener('click', this.closeUserMenu);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeUserMenu);
  },

  methods: {
    collapseFilter() {
      this.filtered = !this.filtered;
    },
    closeUserMenu() {
      this.userMenuOpen = false;
    },
    toggleMode() {
      this.isDark = !this.isDark;
      document.body.className = this.isDark ? 'dark-only' : 'ltr';
      localStorage.setItem('mode', this.isDark ? 'dark-only' : 'light-only');
    },
    async handleLogout() {
      const store = useAuthStore();
      await store.logout();
      this.$router.replace('/auth/login');
    },
    async openMobileSearch() {
      this.mobileSearchOpen = true;
      this.mobileQuery = '';
      this.mobileResults = [];
      await this.$nextTick();
      this.$refs.mobileInput?.focus();
    },
    onMobileSearch() {
      if (!this.mobileQuery) { this.mobileResults = []; return; }
      this.$store.dispatch('menu/searchTerm', this.mobileQuery);
      const auth = useAuthStore();
      const all = this.$store.state.menu.searchData || [];
      this.mobileResults = auth.isSuperAdmin ? all : all.filter(i => !i.path?.startsWith('/super-admin'));
    },
    goMobileFirst() {
      if (this.mobileResults[0]) {
        this.$router.push({ path: this.mobileResults[0].path });
        this.mobileSearchOpen = false;
      }
    },
    mobileHighlight(text) {
      if (!this.mobileQuery) return text;
      const esc = this.mobileQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return text.replace(new RegExp(`(${esc})`, 'gi'), '<mark class="gl-match">$1</mark>');
    },
  },
};
</script>

<style scoped>
.gl-header {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  height: 70px;
  background: #ffffff;
  border-bottom: 1px solid #f0f0f2;
}

/* Brand section — must match --gl-sidebar-width (260px) */
.gl-header-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 260px;
  min-width: 260px;
  padding: 0 24px;
  border-right: 1px solid #f0f0f2;
  align-self: stretch;
  flex-shrink: 0;
}

/* Search */
.gl-header-search {
  flex: 1;
  padding: 0 24px;
  display: flex;
  align-items: center;
}

/* Actions */
.gl-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 20px;
  margin-left: auto;
}

/* Icon buttons */
.gl-icon-btn {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}
.gl-icon-btn:hover {
  background: #f5f5f7;
  color: #111318;
}

/* Notification dot */
.gl-notif-dot {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dc2626;
  border: 1.5px solid #fff;
}

/* Divider */
.gl-divider {
  width: 1px;
  height: 24px;
  background: #e5e7eb;
  margin: 0 8px;
}

/* User menu */
.gl-user-menu {
  position: relative;
}

.gl-user-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px 5px 6px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
  border: 1.5px solid transparent;
  user-select: none;
}
.gl-user-trigger:hover,
.gl-user-menu.open .gl-user-trigger {
  background: #f5f5f7;
  border-color: #e5e7eb;
}

/* Avatar */
.gl-avatar {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: linear-gradient(135deg, #900C27 0%, #6f0a1e 100%);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.gl-avatar.lg {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  font-size: 14px;
}

.gl-user-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.gl-user-name {
  font-size: 13px;
  font-weight: 600;
  color: #111318;
  line-height: 1;
}
.gl-user-role {
  font-size: 11px;
  color: #9ca3af;
  line-height: 1;
  font-weight: 500;
}

.gl-chevron {
  color: #9ca3af;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.gl-chevron.rotated {
  transform: rotate(180deg);
}

/* Dropdown */
.gl-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06);
  z-index: 1000;
  overflow: hidden;
  animation: gl-dropdown-in 0.15s ease;
}

@keyframes gl-dropdown-in {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.gl-dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fafafa;
}
.gl-dd-name {
  font-size: 13px;
  font-weight: 700;
  color: #111318;
  line-height: 1.2;
}
.gl-dd-email {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}

.gl-dropdown-divider {
  height: 1px;
  background: #f0f0f2;
}

.gl-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 11px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s, color 0.12s;
  text-align: left;
}
.gl-dropdown-item:hover {
  background: #f5f5f7;
  color: #111318;
}
.gl-dropdown-item.danger {
  color: #dc2626;
}
.gl-dropdown-item.danger:hover {
  background: #fef2f2;
  color: #b91c1c;
}

/* ── Responsive helpers ────────────────────────── */
.gl-mobile-only  { display: none; }
.gl-desktop-only { display: flex; }

@media (max-width: 991px) {
  .gl-mobile-only  { display: flex; }
  .gl-desktop-only { display: none !important; }

  .gl-header-brand {
    width: auto;
    min-width: auto;
    border-right: none;
    padding: 0 4px 0 16px;
  }
  /* Hide full search bar on mobile */
  .gl-desktop-search { display: none; }

  .gl-header-actions {
    padding: 0 12px 0 0;
    gap: 2px;
    margin-left: auto;
  }
  .gl-user-trigger {
    padding: 4px;
    gap: 0;
  }
}

/* ── Mobile search overlay ─────────────────────── */
.gl-mobile-search-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.4);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.gl-mobile-search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f2;
}
.gl-mobile-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #111318;
  background: transparent;
}
.gl-mobile-search-input::placeholder { color: #9ca3af; }
.gl-mobile-search-close {
  border: none; background: none; cursor: pointer;
  font-size: 14px; font-weight: 600; color: #900C27; padding: 4px 0;
}
.gl-mobile-search-results {
  background: #fff;
  flex: 1;
  overflow-y: auto;
}
.gl-mobile-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f8fafc;
  text-decoration: none;
  transition: background .12s;
}
.gl-mobile-result:hover { background: #f0f8f5; }
.gl-mobile-result-icon {
  width: 34px; height: 34px; border-radius: 9px;
  background: #f1f5f9; color: #64748b;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-mobile-result-title { font-size: 14px; font-weight: 600; color: #1e293b; }
.gl-mobile-result-path  { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.gl-mobile-search-empty {
  background: #fff; padding: 40px 16px;
  text-align: center; font-size: 14px; color: #94a3b8;
}

/* Slide transition */
.gl-search-slide-enter-active { transition: opacity .18s; }
.gl-search-slide-leave-active { transition: opacity .12s; }
.gl-search-slide-enter-from, .gl-search-slide-leave-to { opacity: 0; }

/* Dark mode overlay */
body.dark-only .gl-mobile-search-bar { background: #1a2535; border-color: #243040; }
body.dark-only .gl-mobile-search-input { color: #f1f5f9; }
body.dark-only .gl-mobile-search-results { background: #1a2535; }
body.dark-only .gl-mobile-result { border-color: #1e2d3d; }
body.dark-only .gl-mobile-result:hover { background: rgba(144,12,39,.12); }
body.dark-only .gl-mobile-result-icon { background: #243040; }
body.dark-only .gl-mobile-result-title { color: #f1f5f9; }
body.dark-only .gl-mobile-search-empty { background: #1a2535; }
</style>

<style>
/* Dark mode — not scoped so body.dark-only selector works */
body.dark-only .gl-header {
  background-color: #1e2130;
  border-bottom-color: #323644;
}
body.dark-only .gl-header-brand {
  border-right-color: #323644;
}
body.dark-only .gl-user-name { color: rgba(255,255,255,0.87); }
body.dark-only .gl-user-role { color: rgba(255,255,255,0.40); }
body.dark-only .gl-user-trigger:hover,
body.dark-only .gl-user-menu.open .gl-user-trigger {
  background: rgba(255,255,255,0.07);
  border-color: #323644;
}
body.dark-only .gl-icon-btn { color: rgba(255,255,255,0.55); }
body.dark-only .gl-icon-btn:hover { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.87); }
body.dark-only .gl-divider { background: #323644; }
body.dark-only .gl-dropdown {
  background: #262932;
  border-color: #323644;
  box-shadow: 0 8px 30px rgba(0,0,0,0.40);
}
body.dark-only .gl-dropdown-header { background: #1d1e26; }
body.dark-only .gl-dd-name { color: rgba(255,255,255,0.87); }
body.dark-only .gl-dd-email { color: rgba(255,255,255,0.40); }
body.dark-only .gl-dropdown-item { color: rgba(255,255,255,0.70); }
body.dark-only .gl-dropdown-item:hover { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.87); }
body.dark-only .gl-dropdown-divider { background: #323644; }
body.dark-only .gl-chevron { color: rgba(255,255,255,0.35); }
</style>
